import { Component, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

class PageErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }
  render() {
    if (this.state.error) {
      const err = this.state.error as Error;
      return (
        <div style={{ padding: 32, fontFamily: "monospace" }}>
          <h2 style={{ color: "red" }}>Page crashed — copy this error and send it:</h2>
          <pre style={{ background: "#fee", padding: 16, borderRadius: 8, whiteSpace: "pre-wrap", wordBreak: "break-all", fontSize: 13 }}>
            {err.message}{"\n\n"}{err.stack}
          </pre>
          <button onClick={() => this.setState({ error: null })} style={{ marginTop: 16, padding: "8px 16px", cursor: "pointer" }}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { AuthProvider } from "@/hooks/useAuth";
import { ChurchProvider } from "@/hooks/useChurch";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { FreemiumProvider } from "@/hooks/useFreemium";
import { SidebarStateProvider } from "@/hooks/useSidebarState";
import ProtectedRoute from "@/components/ProtectedRoute";
import SubscriptionGate from "@/components/SubscriptionGate";
import RoleRoute from "@/components/RoleRoute";
import AppLayout from "./components/AppLayout";
import Index from "./pages/Index";
import People from "./pages/People";
import Attendance from "./pages/Attendance";
import EventAttendancePage from "./pages/EventAttendancePage";
import EventsPage from "./pages/EventsPage";
import Finance from "./pages/Finance";
import ProjectBudgetDetailPage from "./pages/ProjectBudgetDetailPage";

import OrganizationsPage from "./pages/OrganizationsPage";
import MinistryDetailPage from "./pages/MinistryDetailPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import AccountPage from "./pages/AccountPage";
import Billing from "./pages/Billing";
import SubscriptionPage from "./pages/SubscriptionPage";
import Login from "./pages/Login";
import InvitePage from "./pages/InvitePage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import CheckinPage from "./pages/CheckinPage";
import EventViewPage from "./pages/EventViewPage";
import NotFound from "./pages/NotFound";
import MissingInfoPage from "./pages/MissingInfoPage";
import BirthdaysPage from "./pages/BirthdaysPage";
import BulletinsPage from "./pages/BulletinsPage";
import BulletinTemplatesPage from "./pages/BulletinTemplatesPage";
import BulletinParticipantDetailPage from "./pages/BulletinParticipantDetailPage";
import LandingPage from "./pages/LandingPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import AboutPage from "./pages/AboutPage";
import ChurchSetupPage from "./pages/ChurchSetupPage";
import AccessRevokedPage from "./pages/AccessRevokedPage";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ChurchProvider>
            <SubscriptionProvider>
              <FreemiumProvider>
              <SidebarStateProvider>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Login initialMode="register" />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/invite" element={<InvitePage />} />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />
                <Route path="/checkin" element={<CheckinPage />} />
                <Route path="/event" element={<PageErrorBoundary><EventViewPage /></PageErrorBoundary>} />
                <Route path="/setup" element={<ProtectedRoute><ChurchSetupPage /></ProtectedRoute>} />
                <Route path="/access-revoked" element={<ProtectedRoute><AccessRevokedPage /></ProtectedRoute>} />
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/billing" element={<RoleRoute feature="billing"><Billing /></RoleRoute>} />
                  <Route path="/subscription" element={<RoleRoute feature="billing"><SubscriptionPage /></RoleRoute>} />
                  <Route path="/subscriptions" element={<Navigate to="/subscription" replace />} />
                  <Route element={<SubscriptionGate><></></SubscriptionGate>} />
                  <Route path="/dashboard" element={<SubscriptionGate><Index /></SubscriptionGate>} />
                  <Route path="/people" element={<SubscriptionGate><RoleRoute feature="members"><People /></RoleRoute></SubscriptionGate>} />
                  <Route path="/members" element={<Navigate to="/people?tab=members" replace />} />
                  <Route path="/families" element={<Navigate to="/people?tab=families" replace />} />
                  <Route path="/events" element={<SubscriptionGate><RoleRoute feature="events"><EventsPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/attendance" element={<SubscriptionGate><RoleRoute feature="attendance"><Attendance /></RoleRoute></SubscriptionGate>} />
                  <Route path="/attendance/event/:eventId" element={<SubscriptionGate><RoleRoute feature="attendance"><EventAttendancePage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/finance" element={<SubscriptionGate><RoleRoute feature="finance"><Finance /></RoleRoute></SubscriptionGate>} />
                  <Route path="/finance/projects/:projectId" element={<SubscriptionGate><RoleRoute feature="finance"><ProjectBudgetDetailPage /></RoleRoute></SubscriptionGate>} />
                  
                  <Route path="/communication" element={<PageErrorBoundary><SubscriptionGate><RoleRoute feature="communication"><BulletinsPage /></RoleRoute></SubscriptionGate></PageErrorBoundary>} />
                  <Route path="/communication/participants/:participantName" element={<PageErrorBoundary><SubscriptionGate><RoleRoute feature="communication"><BulletinParticipantDetailPage /></RoleRoute></SubscriptionGate></PageErrorBoundary>} />
                  <Route path="/communication/templates" element={<PageErrorBoundary><SubscriptionGate><RoleRoute feature="communication"><BulletinTemplatesPage /></RoleRoute></SubscriptionGate></PageErrorBoundary>} />
                  <Route path="/organizations" element={<SubscriptionGate><RoleRoute feature="ministries"><OrganizationsPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/ministries" element={<SubscriptionGate><RoleRoute feature="ministries"><OrganizationsPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/ministries/:id" element={<SubscriptionGate><RoleRoute feature="ministries"><MinistryDetailPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/settings" element={<SubscriptionGate><RoleRoute feature="settings"><SettingsPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
                  <Route path="/missing-info" element={<SubscriptionGate><RoleRoute feature="members"><MissingInfoPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/birthdays" element={<SubscriptionGate><RoleRoute feature="members"><BirthdaysPage /></RoleRoute></SubscriptionGate>} />
                  <Route path="/bulletins" element={<Navigate to="/communication" replace />} />
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
              </SidebarStateProvider>
              </FreemiumProvider>
            </SubscriptionProvider>
          </ChurchProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
